import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  Loader2,
  Save,
  RefreshCw,
  FileJson,
  History,
  Network,
  ShieldCheck,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  RotateCcw,
  Copy,
  LayoutList,
} from "lucide-react";
import { toast } from "sonner";
import SejarahForm from "@/admin/components/SejarahForm";
import StrukturForm from "@/admin/components/StrukturForm";

/* ---------------------------------------------------------------
   Default templates (mirror of DEFAULT values used in pages).
   Used when admin clicks "Muat Template Default" or when a key has
   no stored document yet.
   Keep in sync with:
   - /app/frontend/src/pages/Sejarah.jsx  (DEFAULT_CONTENT)
   - /app/frontend/src/pages/StrukturOrganisasi.jsx (DEFAULT_ORG_DATA)
   --------------------------------------------------------------- */
const DEFAULT_SEJARAH = {
  narasi:
    'Rumah Sakit Bhayangkara Tarakan adalah rumah sakit umum milik Kepolisian Negara Republik Indonesia (Polri) yang berada di bawah naungan Polda Kalimantan Utara. Berdiri di Kota Tarakan sebagai jawaban atas kebutuhan pelayanan kesehatan bagi anggota Polri, Aparatur Sipil Negara (ASN) Polri, keluarga, serta masyarakat umum Kalimantan Utara. Mengedepankan motto "Melayani dengan Sepenuh Hati", rumah sakit terus bertumbuh dari waktu ke waktu — dari poliklinik kesehatan sederhana menjadi rumah sakit terakreditasi Paripurna dengan layanan spesialis terpadu.',
  timeline: [
    { year: "2005", title: "Poliklinik Kesehatan Polri", body: "Cikal-bakal berdiri sebagai poliklinik kesehatan yang melayani anggota Polri dan keluarganya di lingkungan Polda Kaltara." },
    { year: "2010", title: "Peningkatan Status Menjadi Balai Pengobatan", body: "Status ditingkatkan menjadi Balai Pengobatan tingkat lanjut dengan penambahan poli umum, poli gigi, dan farmasi." },
    { year: "2015", title: "Berdiri sebagai Rumah Sakit Bhayangkara", body: "Diresmikan sebagai Rumah Sakit Bhayangkara Tarakan Tk. IV berdasarkan Peraturan Kapolri No. XX/2015, melayani anggota Polri, keluarga, ASN Polri dan masyarakat umum." },
    { year: "2018", title: "Lulus Akreditasi Nasional", body: "Lulus Akreditasi Rumah Sakit dari KARS (Komisi Akreditasi Rumah Sakit) dengan predikat Perdana." },
    { year: "2021", title: "Peningkatan Kelas Menjadi Tipe III", body: "SK Kemenkes RI menetapkan RS Bhayangkara Tarakan naik status ke Tipe III dengan fasilitas rawat inap 60+ tempat tidur." },
    { year: "2024", title: "Akreditasi Paripurna & Layanan Terpadu", body: "Meraih akreditasi Paripurna (bintang 5) LARS-DHP. Melengkapi layanan poli spesialis, laboratorium, radiologi CT-Scan, ICU, dan kamar operasi mayor." },
  ],
  perizinan: [
    { icon: "FileBadge2", title: "Izin Operasional", detail: "SK Kemenkes RI No. HK.01.07/MENKES/2021", valid: "Berlaku 5 tahun • diperpanjang berkala" },
    { icon: "ShieldCheck", title: "SK Pendirian", detail: "Peraturan Kapolri No. XX Tahun 2015", valid: "Ditetapkan di Jakarta • 2015" },
    { icon: "Award", title: "Akreditasi", detail: "LARS-DHP • Paripurna (Bintang 5)", valid: "Berlaku 2024 – 2027" },
    { icon: "Building2", title: "NIB & OSS", detail: "Nomor Induk Berusaha aktif via OSS-RBA", valid: "Terintegrasi Kemenkes" },
  ],
  klasifikasi: {
    tipe: "Rumah Sakit Umum Kelas C (Tipe III Polri)",
    kepemilikan: "Kepolisian Negara Republik Indonesia (Polri)",
    jenis: "Rumah Sakit Umum",
    kapasitas: "60+ Tempat Tidur",
    igd: "24 Jam / 7 Hari",
    layanan: [
      "Pelayanan Medik Umum",
      "Pelayanan Gawat Darurat",
      "Pelayanan Medik Spesialis Dasar",
      "Pelayanan Penunjang Medik",
      "Pelayanan Keperawatan & Kebidanan",
      "Pelayanan Farmasi",
      "Pelayanan Rehabilitasi Medik",
    ],
  },
  photos: [
    { id: "ceremony-1", src: "https://images.unsplash.com/photo-1587351021355-a479a299d2f9?w=1200&q=80&auto=format&fit=crop", caption: "Peresmian Gedung Utama RS Bhayangkara Tarakan — 2015", subtitle: "Penandatanganan prasasti oleh Kapolda Kaltara" },
    { id: "ceremony-2", src: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200&q=80&auto=format&fit=crop", caption: "Pemotongan Pita Peresmian", subtitle: "Bersama unsur Muspida Kota Tarakan" },
    { id: "ceremony-3", src: "https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?w=1200&q=80&auto=format&fit=crop", caption: "Prasasti Peresmian Rumah Sakit", subtitle: "Batu prasasti bertuliskan tanggal peresmian" },
  ],
};

const DEFAULT_STRUKTUR = {
  id: "karumkit",
  title: "Kepala Rumah Sakit",
  short: "Karumkit",
  name: "dr. [Nama Pejabat], Sp.PD, M.Kes",
  rank: "AKBP",
  tint: "from-blue-700 to-blue-900",
  children: [
    {
      id: "wakarumkit",
      title: "Wakil Kepala Rumah Sakit",
      short: "Wakarumkit",
      name: "dr. [Nama Pejabat], Sp.B",
      rank: "KOMPOL",
      tint: "from-blue-600 to-blue-800",
      children: [
        {
          id: "yanmedwat",
          title: "Pelayanan Medis & Perawatan",
          short: "Kaur Yanmedwat",
          name: "dr. [Nama], Sp.PK",
          rank: "AKP",
          tint: "from-emerald-500 to-emerald-700",
          icon: "Stethoscope",
          children: [
            { id: "u-igd", title: "Instalasi Gawat Darurat", short: "IGD 24 Jam", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-ri", title: "Instalasi Rawat Inap", short: "Rawat Inap", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-rj", title: "Instalasi Rawat Jalan / Poliklinik", short: "Rawat Jalan", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-icu", title: "Instalasi ICU", short: "ICU", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-ok", title: "Instalasi Bedah Sentral (OK)", short: "Kamar Operasi", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-kebidanan", title: "Instalasi Kebidanan & Kandungan", short: "VK / OBGYN", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
          ],
        },
        {
          id: "jangmedum",
          title: "Penunjang Medis & Umum",
          short: "Kaur Jangmedum",
          name: "[Nama], S.Kep",
          rank: "AKP",
          tint: "from-amber-500 to-amber-700",
          icon: "FlaskConical",
          children: [
            { id: "j-farmasi", title: "Instalasi Farmasi", short: "Farmasi", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-rad", title: "Instalasi Radiologi", short: "Radiologi", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-lab", title: "Instalasi Laboratorium", short: "Lab Klinik", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-gizi", title: "Instalasi Gizi", short: "Gizi", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-rm", title: "Rekam Medis & SIRS", short: "Rekam Medis", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-cssd", title: "CSSD & Loundry", short: "CSSD", tint: "bg-amber-50 border-amber-200 text-amber-800" },
          ],
        },
      ],
    },
    {
      id: "wasintern",
      title: "Pengawasan Internal",
      short: "Kabag Wasintern",
      name: "[Nama], S.H",
      rank: "AKP",
      tint: "from-red-500 to-red-700",
      icon: "ShieldCheck",
      children: [
        { id: "w-etik", title: "Komite Etik & Hukum", short: "Etik & Hukum", tint: "bg-red-50 border-red-200 text-red-800" },
        { id: "w-audit", title: "Audit Mutu & PMKP", short: "PMKP", tint: "bg-red-50 border-red-200 text-red-800" },
      ],
    },
    {
      id: "renmin",
      title: "Perencanaan & Administrasi",
      short: "Kabag Renmin",
      name: "[Nama], S.E",
      rank: "KOMPOL",
      tint: "from-slate-600 to-slate-800",
      icon: "Users",
      children: [
        { id: "r-sdm", title: "Urusan SDM & Personel", short: "SDM", tint: "bg-slate-50 border-slate-200 text-slate-800" },
        { id: "r-keu", title: "Urusan Keuangan", short: "Keuangan", tint: "bg-slate-50 border-slate-200 text-slate-800" },
        { id: "r-log", title: "Urusan Logistik & BMN", short: "Logistik", tint: "bg-slate-50 border-slate-200 text-slate-800" },
        { id: "r-tu", title: "Tata Usaha & Umum", short: "Tata Usaha", tint: "bg-slate-50 border-slate-200 text-slate-800" },
      ],
    },
    {
      id: "binfung",
      title: "Pembinaan Fungsi",
      short: "Kabag Binfung",
      name: "dr. [Nama], M.Kes",
      rank: "KOMPOL",
      tint: "from-purple-600 to-purple-800",
      children: [
        { id: "b-diklat", title: "Diklat & Litbang", short: "Diklat", tint: "bg-purple-50 border-purple-200 text-purple-800" },
        { id: "b-humas", title: "Humas & Kerja Sama", short: "Humas", tint: "bg-purple-50 border-purple-200 text-purple-800" },
      ],
    },
  ],
};

const TABS = [
  {
    key: "sejarah",
    label: "Sejarah",
    icon: History,
    previewPath: "/profil/sejarah",
    template: DEFAULT_SEJARAH,
    Form: SejarahForm,
  },
  {
    key: "struktur_organisasi",
    label: "Struktur Organisasi",
    icon: Network,
    previewPath: "/profil/struktur-organisasi",
    template: DEFAULT_STRUKTUR,
    Form: StrukturForm,
  },
];

const clone = (obj) => JSON.parse(JSON.stringify(obj));

const SiteContentPanel = () => {
  const { authAxios, user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "superadmin");

  const [tabKey, setTabKey] = useState("sejarah");
  const [mode, setMode] = useState("form"); // "form" | "json"
  const [doc, setDoc] = useState(null); // canonical object
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  const activeTab = useMemo(() => TABS.find((t) => t.key === tabKey), [tabKey]);

  const loadFromServer = async (key) => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/site-content/${key}`);
      const template = TABS.find((t) => t.key === key)?.template;
      const nextDoc = data && data.doc ? data.doc : clone(template);
      setDoc(nextDoc);
      setJsonText(JSON.stringify(nextDoc, null, 2));
      setJsonError(null);
      setLastUpdatedAt(data?.updated_at || null);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat konten");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadFromServer(tabKey);
  }, [tabKey, isAdmin]);

  const confirmDiscard = () =>
    !dirty || window.confirm("Perubahan yang belum disimpan akan hilang. Lanjutkan?");

  // Form edits update the canonical doc
  const handleFormChange = (nextDoc) => {
    setDoc(nextDoc);
    setDirty(true);
  };

  // JSON edits: keep raw text, parse into doc when valid
  const handleJsonChange = (v) => {
    setJsonText(v);
    setDirty(true);
    try {
      const parsed = JSON.parse(v);
      setDoc(parsed);
      setJsonError(null);
    } catch (e) {
      setJsonError(e.message);
    }
  };

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    if (nextMode === "json") {
      // serialize the current doc to text
      setJsonText(JSON.stringify(doc ?? {}, null, 2));
      setJsonError(null);
    } else {
      // going back to form — if JSON is broken, block
      if (jsonError) {
        toast.error(`Perbaiki JSON dulu: ${jsonError}`);
        return;
      }
    }
    setMode(nextMode);
  };

  const handleSave = async () => {
    if (mode === "json" && jsonError) {
      toast.error(`JSON tidak valid: ${jsonError}`);
      return;
    }
    if (doc === null || doc === undefined) {
      toast.error("Tidak ada konten untuk disimpan");
      return;
    }
    setSaving(true);
    try {
      const { data } = await authAxios.put(`/site-content/${tabKey}`, { doc });
      setLastUpdatedAt(data?.updated_at || new Date().toISOString());
      setDirty(false);
      toast.success("Konten berhasil disimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan konten");
    } finally {
      setSaving(false);
    }
  };

  const handleLoadDefault = () => {
    if (!confirmDiscard()) return;
    const next = clone(activeTab.template);
    setDoc(next);
    setJsonText(JSON.stringify(next, null, 2));
    setJsonError(null);
    setDirty(true);
  };

  const handleReloadFromServer = () => {
    if (!confirmDiscard()) return;
    loadFromServer(tabKey);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(doc ?? {}, null, 2));
      toast.success("JSON disalin ke clipboard");
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const handleTabChange = (key) => {
    if (!confirmDiscard()) return;
    setTabKey(key);
  };

  if (!isAdmin) {
    return (
      <div
        data-testid="admin-site-content-forbidden"
        className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800"
      >
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <div className="font-heading font-semibold">Akses Terbatas</div>
        </div>
        <div className="text-sm">Editor Konten Profil hanya dapat diakses oleh admin.</div>
      </div>
    );
  }

  const ActiveForm = activeTab.Form;

  return (
    <div data-testid="admin-panel-site-content" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <LayoutList className="h-6 w-6 text-blue-800" />
            Konten Profil
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Kelola konten halaman <b>Sejarah</b> dan <b>Struktur Organisasi</b> lewat formulir —
            tanpa perlu mengedit JSON.
          </p>
        </div>
        <a
          href={activeTab.previewPath}
          target="_blank"
          rel="noreferrer"
          data-testid="site-content-preview-link"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-slate-900 text-white hover:bg-slate-800"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Buka Preview di Tab Baru
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          const isActive = tb.key === tabKey;
          return (
            <button
              key={tb.key}
              type="button"
              data-testid={`site-content-tab-${tb.key}`}
              onClick={() => handleTabChange(tb.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition ${
                isActive
                  ? "border-blue-800 text-blue-800"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tb.label}
            </button>
          );
        })}
      </div>

      {/* Mode switch + toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full bg-slate-100 p-0.5" data-testid="site-content-mode-switch">
          <button
            type="button"
            data-testid="site-content-mode-form"
            onClick={() => switchMode("form")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              mode === "form" ? "bg-white shadow text-blue-800" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Formulir
          </button>
          <button
            type="button"
            data-testid="site-content-mode-json"
            onClick={() => switchMode("json")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              mode === "json" ? "bg-white shadow text-blue-800" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            JSON (Lanjutan)
          </button>
        </div>

        <div className="h-5 w-px bg-slate-200 mx-1" />

        <button
          type="button"
          onClick={handleReloadFromServer}
          data-testid="site-content-reload-btn"
          disabled={loading || saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Muat Ulang
        </button>
        <button
          type="button"
          onClick={handleLoadDefault}
          data-testid="site-content-load-default-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Template Default
        </button>
        <button
          type="button"
          onClick={handleCopy}
          data-testid="site-content-copy-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
        >
          <Copy className="h-3.5 w-3.5" />
          Salin JSON
        </button>

        <div className="flex-1" />

        {lastUpdatedAt && (
          <span className="text-xs text-slate-400" data-testid="site-content-last-updated">
            Terakhir diperbarui: {new Date(lastUpdatedAt).toLocaleString("id-ID")}
          </span>
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || (mode === "json" && !!jsonError) || !dirty}
          data-testid="site-content-save-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-blue-800 hover:bg-blue-900 text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Simpan
        </button>
      </div>

      {dirty && (
        <div className="text-xs">
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">
            Ada perubahan belum disimpan
          </span>
        </div>
      )}

      {/* Body */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center rounded-2xl">
            <Loader2 className="h-6 w-6 animate-spin text-blue-800" />
          </div>
        )}

        {mode === "form" ? (
          doc ? (
            <ActiveForm value={doc} onChange={handleFormChange} />
          ) : (
            <div className="text-sm text-slate-400 py-10 text-center">Memuat…</div>
          )
        ) : (
          <div className="space-y-3">
            <div
              data-testid="site-content-json-status"
              className={`flex items-center gap-2 text-xs font-medium ${
                jsonError ? "text-red-700" : "text-emerald-700"
              }`}
            >
              {jsonError ? (
                <>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>Error: {jsonError}</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>JSON valid</span>
                </>
              )}
            </div>
            <textarea
              value={jsonText}
              onChange={(e) => handleJsonChange(e.target.value)}
              spellCheck={false}
              data-testid="site-content-json-editor"
              className={`w-full min-h-[520px] font-mono text-xs leading-relaxed p-4 rounded-2xl border shadow-inner focus:outline-none focus:ring-2 bg-slate-950 text-slate-100 ${
                jsonError ? "border-red-300 focus:ring-red-500" : "border-slate-200 focus:ring-blue-500"
              }`}
              placeholder='{ "narasi": "..." }'
            />
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <FileJson className="h-3.5 w-3.5" />
              Mode lanjutan — perubahan di sini otomatis tersinkron ke formulir bila JSON valid.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteContentPanel;
